// src/components/Dashboard/CRMDashboard.tsx
// ============================================
// CRM Dashboard - 第三页：客户流水线（Sprint 8 Epic 4）
//
// 布局：
//   - 顶部：CRM 总览 KPI（总客户/总金额/快成交数/长期未联系数）
//   - 中部：Pipeline 可视化（10 阶段横向流水线）
//       Lead → Opportunity → Quotation → Contract → Invoice → Payment
//         → Project → Service → Renew → Referral
//   - 底部：Top 5 高优先级客户列表 + 本周预计成交清单
//
// 数据来源：useDashboardData.crm
// ============================================

import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Users,
  DollarSign,
  Flame,
  AlertCircle,
  ChevronRight,
  Trophy,
} from "lucide-react";
import {
  DashboardCard,
  KpiCell,
  EmptyState,
  FadeIn,
  Tag,
  formatYuanWithSymbol,
} from "./shared";
import type { CRMData } from "../../hooks/useDashboardData";

interface CRMDashboardProps {
  data: CRMData;
}

export default function CRMDashboard({ data }: CRMDashboardProps) {
  const { pipeline, priorityCustomers, closingThisWeek, stats, degraded } = data;

  // Pipeline 汇总
  const pipelineTotal = useMemo(
    () => pipeline.reduce((s, p) => s + p.count, 0),
    [pipeline]
  );
  const pipelineAmount = useMemo(
    () => pipeline.reduce((s, p) => s + p.amount, 0),
    [pipeline]
  );
  const maxCount = useMemo(
    () => pipeline.reduce((max, s) => Math.max(max, s.count), 0) || 1,
    [pipeline]
  );

  // 顶部 KPI
  const totalCustomers = stats?.total ?? 0;
  const totalValue = stats?.totalEstimatedValue ?? pipelineAmount;
  const closingCount = stats?.closingCount ?? closingThisWeek.length;
  const staleCount = stats?.staleCount ?? 0;

  return (
    <div className="space-y-3">
      {/* ============================================ */}
      {/* 顶部：CRM 总览 KPI（4 列） */}
      {/* ============================================ */}
      <FadeIn>
        <div className="grid grid-cols-4 gap-2.5">
          <KpiCell
            label="总客户数"
            value={totalCustomers}
            unit="人"
            color="#3B82F6"
            subtitle="Total Customers"
            degraded={degraded && totalCustomers === 0}
          />
          <KpiCell
            label="总预估金额"
            value={totalValue}
            format={(v) => formatYuanWithSymbol(v)}
            color="#10B981"
            subtitle="Pipeline Value"
            degraded={degraded && totalValue === 0}
          />
          <KpiCell
            label="快成交客户"
            value={closingCount}
            unit="个"
            color="#F59E0B"
            subtitle="Closing Soon"
            degraded={degraded && closingCount === 0}
          />
          <KpiCell
            label="长期未联系"
            value={staleCount}
            unit="个"
            color="#EF4444"
            subtitle="Stale > 30D"
            degraded={degraded && staleCount === 0}
          />
        </div>
      </FadeIn>

      {/* ============================================ */}
      {/* 中部：Pipeline 可视化（10 阶段流水线） */}
      {/* ============================================ */}
      <FadeIn delay={0.05}>
        <DashboardCard
          title="客户流水线"
          subtitle={`Pipeline · ${pipelineTotal} deals · ¥${(pipelineAmount / 10000).toFixed(1)}w`}
          accentColor="#3B82F6"
          extra={<Tag color="blue">{pipeline.length} STAGES</Tag>}
          bodyClassName="min-h-[200px]"
        >
          {degraded ? (
            <EmptyState degraded />
          ) : (
            <div className="space-y-2">
              {/* 阶段卡片网格 */}
              <div className="grid grid-cols-10 gap-1.5">
                {pipeline.map((s, i) => {
                  const widthPct = Math.max(
                    10,
                    (s.count / maxCount) * 100
                  );
                  return (
                    <motion.div
                      key={s.stage}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04, duration: 0.3 }}
                      className="relative flex flex-col"
                    >
                      {/* 阶段卡片 */}
                      <div className="relative rounded-md border border-[#1e2a3f]/70 bg-[#0a0e1a]/60 px-1.5 py-2 overflow-hidden group hover:border-blue-500/50 transition-colors">
                        <div
                          className="absolute top-0 left-0 right-0 h-px"
                          style={{
                            background: `linear-gradient(90deg, #3B82F6, #06B6D4)`,
                            opacity: 0.4 + (i / pipeline.length) * 0.6,
                          }}
                        />
                        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider text-center">
                          {s.label}
                        </div>
                        <div className="text-[16px] font-mono font-bold text-white text-center mt-0.5">
                          {s.count}
                        </div>
                        <div className="text-[9px] font-mono text-emerald-400 text-center">
                          ¥{(s.amount / 10000).toFixed(1)}w
                        </div>
                        {/* 进度条 */}
                        <div className="mt-1 h-1 bg-[#0a0e1a] rounded-sm overflow-hidden">
                          <div
                            className="h-full"
                            style={{
                              width: `${widthPct}%`,
                              background:
                                "linear-gradient(90deg, #3B82F6, #06B6D4)",
                              opacity: 0.6 + (i / pipeline.length) * 0.4,
                            }}
                          />
                        </div>
                      </div>
                      {/* 阶段连接箭头 */}
                      {i < pipeline.length - 1 && (
                        <div className="absolute top-1/2 -right-1.5 -translate-y-1/2 z-10">
                          <ChevronRight className="w-2.5 h-2.5 text-gray-600" />
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>

              {/* 转化率提示 */}
              <div className="mt-3 px-2 py-1.5 rounded border border-[#1e2a3f]/40 bg-[#0a0e1a]/40">
                <div className="flex items-center justify-between text-[10px] font-mono">
                  <span className="text-gray-500">转化漏斗</span>
                  <span className="text-gray-400">
                    线索 → 成交：{" "}
                    <span className="text-emerald-400 font-semibold">
                      {pipeline[0] && pipeline[5]
                        ? (
                            (pipeline[5].count / Math.max(pipeline[0].count, 1)) *
                            100
                          ).toFixed(1)
                        : "0"}
                        %
                    </span>
                  </span>
                </div>
              </div>
            </div>
          )}
        </DashboardCard>
      </FadeIn>

      {/* ============================================ */}
      {/* 底部：Top 5 高优先级客户 + 本周预计成交 */}
      {/* ============================================ */}
      <div className="grid grid-cols-2 gap-3">
        {/* Top 5 高优先级客户 */}
        <FadeIn delay={0.1}>
          <DashboardCard
            title="高优先级客户 Top 5"
            subtitle="Priority Customers"
            accentColor="#EC4899"
            extra={<Trophy className="w-3 h-3 text-pink-400" />}
            bodyClassName="min-h-[200px]"
          >
            {degraded && priorityCustomers.length === 0 ? (
              <EmptyState degraded />
            ) : priorityCustomers.length === 0 ? (
              <EmptyState message="暂无高优先级客户" />
            ) : (
              <div className="space-y-0.5">
                <div className="grid grid-cols-12 gap-2 px-2 py-1 text-[9px] font-mono text-gray-600 uppercase tracking-wider border-b border-[#1e2a3f]/40">
                  <span className="col-span-4">公司</span>
                  <span className="col-span-1">等级</span>
                  <span className="col-span-3">负责人</span>
                  <span className="col-span-2">下次联系</span>
                  <span className="col-span-2 text-right">预估金额</span>
                </div>
                {priorityCustomers.map((c, i) => (
                  <div
                    key={c.id}
                    className="grid grid-cols-12 gap-2 px-2 py-1.5 items-center text-[10px] font-mono border-b border-[#1e2a3f]/40 last:border-b-0 hover:bg-white/[0.02] rounded"
                  >
                    <div className="col-span-4 flex items-center gap-1.5 min-w-0">
                      <span className="text-gray-600 w-3">{i + 1}</span>
                      <span className="text-gray-200 truncate">
                        {c.company}
                      </span>
                    </div>
                    <span className="col-span-1">
                      <Tag
                        color={
                          c.tier === "S+"
                            ? "purple"
                            : c.tier === "S"
                            ? "pink"
                            : c.tier === "A"
                            ? "blue"
                            : "gray"
                        }
                      >
                        {c.tier}
                      </Tag>
                    </span>
                    <span className="col-span-3 text-gray-400 truncate">
                      {c.owner}
                    </span>
                    <span className="col-span-2 text-gray-400">
                      {c.nextContact || "—"}
                    </span>
                    <span className="col-span-2 text-right text-emerald-400 font-semibold">
                      ¥{(c.estimatedValue / 10000).toFixed(1)}w
                    </span>
                  </div>
                ))}
              </div>
            )}
          </DashboardCard>
        </FadeIn>

        {/* 本周预计成交 */}
        <FadeIn delay={0.15}>
          <DashboardCard
            title="本周预计成交"
            subtitle="Closing This Week"
            accentColor="#10B981"
            extra={<Flame className="w-3 h-3 text-emerald-400" />}
            bodyClassName="min-h-[200px]"
          >
            {degraded && closingThisWeek.length === 0 ? (
              <EmptyState degraded />
            ) : closingThisWeek.length === 0 ? (
              <EmptyState message="本周暂无预计成交" />
            ) : (
              <div className="space-y-1">
                {closingThisWeek.map((d, i) => (
                  <div
                    key={d.id}
                    className="flex items-center gap-2 px-2 py-1.5 border-b border-[#1e2a3f]/40 last:border-b-0 hover:bg-white/[0.02] rounded"
                  >
                    <span className="text-[10px] font-mono text-gray-600 w-4">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-mono text-gray-200 truncate">
                        {d.company}
                      </div>
                      <div className="text-[9px] font-mono text-gray-600">
                        预计成交：{d.expectedCloseDate}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[11px] font-mono font-bold text-emerald-400">
                        ¥{(d.amount / 10000).toFixed(1)}w
                      </div>
                      <div className="text-[9px] font-mono text-gray-500">
                        概率 {d.probability}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DashboardCard>
        </FadeIn>
      </div>

      {/* ============================================ */}
      {/* 底部图例 */}
      {/* ============================================ */}
      <FadeIn delay={0.2}>
        <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-[#1e2a3f]/50 bg-[#0a0e1a]/40">
          <div className="flex items-center gap-3 text-[10px] font-mono">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3 text-blue-400" />
              <span className="text-gray-400">Customers</span>
            </span>
            <span className="flex items-center gap-1">
              <DollarSign className="w-3 h-3 text-emerald-400" />
              <span className="text-gray-400">Value</span>
            </span>
            <span className="flex items-center gap-1">
              <Flame className="w-3 h-3 text-amber-400" />
              <span className="text-gray-400">Closing</span>
            </span>
            <span className="flex items-center gap-1">
              <AlertCircle className="w-3 h-3 text-red-400" />
              <span className="text-gray-400">Stale</span>
            </span>
          </div>
          {degraded && <Tag color="amber">DEGRADED MODE</Tag>}
        </div>
      </FadeIn>
    </div>
  );
}
